// EntranceAnalysisDefaultData.js
// 32 MahaVastu Entrance Zones — 4 fields: zone, remedies, impact, block

// Every predefined `impact`/`block` line now carries all 8 supported
// languages instead of a plain English string — read via
// pickLang(value, lang) from i18n/languageOptions.js at the point of use
// (see buildEntranceHtml in EntranceAnalysis.jsx). `zone` (the N1/E1/...
// technical code) and `remedies` (the internal 'positive'/'negative'/
// 'neutral' key, used for matching/styling) are deliberately left as plain
// strings — only their DISPLAYED label is translated, via
// REMEDY_TYPE_LABELS below.
const tr = (en, hi, gu, mr, bn, kn, ta, te) => ({ en, hi, gu, mr, bn, kn, ta, te });

export const REMEDY_TYPE_LABELS = {
  positive: tr('positive', 'सकारात्मक', 'સકારાત્મક', 'सकारात्मक', 'ইতিবাচক', 'ಸಕಾರಾತ್ಮಕ', 'நேர்மறை', 'సానుకూలం'),
  negative: tr('negative', 'नकारात्मक', 'નકારાત્મક', 'नकारात्मक', 'নেতিবাচক', 'ನಕಾರಾತ್ಮಕ', 'எதிர்மறை', 'ప్రతికూలం'),
  neutral:  tr('neutral',  'तटस्थ',    'તટસ્થ',      'तटस्थ',    'নিরপেক্ষ', 'ತಟಸ್ಥ',      'நடுநிலை',   'తటస్థం'),
};

// Fixed section labels used by buildEntranceHtml() in EntranceAnalysis.jsx.
export const ENTRANCE_SECTION_LABELS = {
  zone:       tr('Zone',        'ज़ोन',        'ઝોન',        'झोन',        'জোন',            'ವಲಯ',          'மண்டலம்',       'జోన్'),
  energyType: tr('Energy Type', 'ऊर्जा प्रकार', 'ઊર્જા પ્રકાર', 'ऊर्जा प्रकार', 'শক্তির ধরন',       'ಶಕ್ತಿಯ ಪ್ರಕಾರ', 'ஆற்றல் வகை',    'శక్తి రకం'),
  impact:     tr('Impact',      'प्रभाव',       'અસર',         'परिणाम',      'প্রভাব',          'ಪರಿಣಾಮ',        'தாக்கம்',        'ప్రభావం'),
  remedies:   tr('Remedies',    'उपाय',        'ઉપાયો',       'उपाय',        'প্রতিকার',         'ಪರಿಹಾರಗಳು',     'பரிகாரங்கள்',    'పరిష్కారాలు'),
};

// "Virtual Entries Remedies" — extra section shown under an entrance's
// Remedies for the zones below (client-supplied list). Language versions of
// the heading and the "Open with <metal> Rod" sentence are machine-written
// and still need a native-speaker review.
export const VIRTUAL_REMEDY_LABEL = tr(
  'Virtual Entries Remedies', 'वर्चुअल एंट्री उपाय', 'વર્ચ્યુઅલ એન્ટ્રી ઉપાયો', 'व्हर्च्युअल एंट्री उपाय',
  'ভার্চুয়াল এন্ট্রি প্রতিকার', 'ವರ್ಚುವಲ್ ಎಂಟ್ರಿ ಪರಿಹಾರಗಳು', 'மெய்நிகர் நுழைவு பரிகாரங்கள்', 'వర్చువల్ ఎంట్రీ నివారణలు');

const ROD_METAL = {
  aluminum: tr('Aluminum', 'एल्युमिनियम', 'એલ્યુમિનિયમ', 'अ‍ॅल्युमिनियम', 'অ্যালুমিনিয়াম', 'ಅಲ್ಯೂಮಿನಿಯಂ', 'அலுமினியம்', 'అల్యూమినియం'),
  ss:       tr('SS', 'एसएस', 'એસએસ', 'एसएस', 'এসএস', 'ಎಸ್‌ಎಸ್', 'எஸ்எஸ்', 'ఎస్‌ఎస్'),
  copper:   tr('Copper', 'तांबे', 'તાંબા', 'तांब्याच्या', 'তামার', 'ತಾಮ್ರ', 'செம்பு', 'రాగి'),
  iron:     tr('Iron', 'लोहे', 'લોખંડ', 'लोखंडी', 'লোহার', 'ಕಬ್ಬಿಣ', 'இரும்பு', 'ఇనుము'),
};
const openWithRod = (m) => tr(
  `Open with ${m.en} Rod`,
  `${m.hi} की रॉड से खोलें`,
  `${m.gu} રોડથી ખોલો`,
  `${m.mr} रॉडने उघडा`,
  `${m.bn} রড দিয়ে খুলুন`,
  `${m.kn} ರಾಡ್‌ನಿಂದ ತೆರೆಯಿರಿ`,
  `${m.ta} கம்பியால் திறக்கவும்`,
  `${m.te} రాడ్‌తో తెరవండి`);

export const VIRTUAL_ENTRY_REMEDIES = {
  N3: openWithRod(ROD_METAL.aluminum),
  N4: openWithRod(ROD_METAL.aluminum),
  N5: openWithRod(ROD_METAL.aluminum),
  N8: openWithRod(ROD_METAL.aluminum),
  E3: openWithRod(ROD_METAL.ss),
  E4: openWithRod(ROD_METAL.ss),
  S2: openWithRod(ROD_METAL.copper),
  S3: openWithRod(ROD_METAL.copper),
  S4: openWithRod(ROD_METAL.copper),
  W3: openWithRod(ROD_METAL.iron),
  W4: openWithRod(ROD_METAL.iron),
};

// Only 6 distinct block-remedy sentences repeat across all 32 zones —
// defined once here and referenced by every row below instead of retyping
// (and re-translating) the same sentence dozens of times.
const BLOCK = {
  IRON_WHITE: tr(
    'Same can be blocked by Iron strip and white color/tape.',
    'इसे लोहे की पट्टी और सफेद रंग/टेप से रोका जा सकता है।',
    'આને લોખંડની પટ્ટી અને સફેદ રંગ/ટેપ વડે અવરોધી શકાય છે.',
    'हे लोखंडी पट्टी आणि पांढऱ्या रंग/टेपने रोखता येते.',
    'এটি লোহার স্ট্রিপ এবং সাদা রঙ/টেপ দিয়ে ব্লক করা যেতে পারে।',
    'ಇದನ್ನು ಕಬ್ಬಿಣದ ಪಟ್ಟಿ ಮತ್ತು ಬಿಳಿ ಬಣ್ಣ/ಟೇಪ್‌ನಿಂದ ತಡೆಯಬಹುದು.',
    'இதை இரும்பு பட்டை மற்றும் வெள்ளை நிற டேப்பால் தடுக்கலாம்.',
    'దీన్ని ఇనుప పట్టీ మరియు తెలుపు రంగు/టేప్‌తో నిరోధించవచ్చు.'),
  IDEAL: tr(
    'This entrance is ideal for entry, no need to block it.',
    'यह प्रवेश द्वार प्रवेश के लिए उत्तम है, इसे रोकने की आवश्यकता नहीं है।',
    'આ પ્રવેશદ્વાર પ્રવેશ માટે આદર્શ છે, તેને અવરોધવાની જરૂર નથી.',
    'हे प्रवेशद्वार प्रवेशासाठी उत्तम आहे, ते रोखण्याची गरज नाही.',
    'এই প্রবেশদ্বার প্রবেশের জন্য আদর্শ, এটি ব্লক করার প্রয়োজন নেই।',
    'ಈ ಪ್ರವೇಶದ್ವಾರ ಪ್ರವೇಶಕ್ಕೆ ಸೂಕ್ತವಾಗಿದೆ, ಇದನ್ನು ತಡೆಯುವ ಅಗತ್ಯವಿಲ್ಲ.',
    'இந்த நுழைவாயில் நுழைவதற்கு ஏற்றது, இதை தடுக்க வேண்டிய அவசியமில்லை.',
    'ఈ ప్రవేశ ద్వారం ప్రవేశానికి అనువైనది, దీన్ని అడ్డుకోవాల్సిన అవసరం లేదు.'),
  ALU_BLUE: tr(
    'Same can be blocked by Aluminium strip and blue color/tape.',
    'इसे एल्युमिनियम पट्टी और नीले रंग/टेप से रोका जा सकता है।',
    'આને એલ્યુમિનિયમની પટ્ટી અને વાદળી રંગ/ટેપ વડે અવરોધી શકાય છે.',
    'हे अ‍ॅल्युमिनियम पट्टी आणि निळ्या रंग/टेपने रोखता येते.',
    'এটি অ্যালুমিনিয়াম স্ট্রিপ এবং নীল রঙ/টেপ দিয়ে ব্লক করা যেতে পারে।',
    'ಇದನ್ನು ಅಲ್ಯೂಮಿನಿಯಂ ಪಟ್ಟಿ ಮತ್ತು ನೀಲಿ ಬಣ್ಣ/ಟೇಪ್‌ನಿಂದ ತಡೆಯಬಹುದು.',
    'இதை அலுமினியம் பட்டை மற்றும் நீல நிற டேப்பால் தடுக்கலாம்.',
    'దీన్ని అల్యూమినియం పట్టీ మరియు నీలం రంగు/టేప్‌తో నిరోధించవచ్చు.'),
  SS_GREEN: tr(
    'Same can be blocked by SS strip and green color/tape.',
    'इसे स्टेनलेस स्टील पट्टी और हरे रंग/टेप से रोका जा सकता है।',
    'આને સ્ટેનલેસ સ્ટીલની પટ્ટી અને લીલા રંગ/ટેપ વડે અવરોધી શકાય છે.',
    'हे स्टेनलेस स्टील पट्टी आणि हिरव्या रंग/टेपने रोखता येते.',
    'এটি স্টেইনলেস স্টিল স্ট্রিপ এবং সবুজ রঙ/টেপ দিয়ে ব্লক করা যেতে পারে।',
    'ಇದನ್ನು ಸ್ಟೇನ್‌ಲೆಸ್ ಸ್ಟೀಲ್ ಪಟ್ಟಿ ಮತ್ತು ಹಸಿರು ಬಣ್ಣ/ಟೇಪ್‌ನಿಂದ ತಡೆಯಬಹುದು.',
    'இதை ஸ்டெயின்லெஸ் ஸ்டீல் பட்டை மற்றும் பச்சை நிற டேப்பால் தடுக்கலாம்.',
    'దీన్ని స్టెయిన్‌లెస్ స్టీల్ పట్టీ మరియు ఆకుపచ్చ రంగు/టేప్‌తో నిరోధించవచ్చు.'),
  BRASS_YELLOW: tr(
    'Same can be blocked by brass strip and yellow color/tape.',
    'इसे पीतल की पट्टी और पीले रंग/टेप से रोका जा सकता है।',
    'આને પિત્તળની પટ્ટી અને પીળા રંગ/ટેપ વડે અવરોધી શકાય છે.',
    'हे पितळी पट्टी आणि पिवळ्या रंग/टेपने रोखता येते.',
    'এটি পিতলের স্ট্রিপ এবং হলুদ রঙ/টেপ দিয়ে ব্লক করা যেতে পারে।',
    'ಇದನ್ನು ಹಿತ್ತಾಳೆ ಪಟ್ಟಿ ಮತ್ತು ಹಳದಿ ಬಣ್ಣ/ಟೇಪ್‌ನಿಂದ ತಡೆಯಬಹುದು.',
    'இதை பித்தளை பட்டை மற்றும் மஞ்சள் நிற டேப்பால் தடுக்கலாம்.',
    'దీన్ని ఇత్తడి పట్టీ మరియు పసుపు రంగు/టేప్‌తో నిరోధించవచ్చు.'),
  COPPER_RED: tr(
    'Same can be blocked by copper strip and red color/tape.',
    'इसे तांबे की पट्टी और लाल रंग/टेप से रोका जा सकता है।',
    'આને તાંબાની પટ્ટી અને લાલ રંગ/ટેપ વડે અવરોધી શકાય છે.',
    'हे तांब्याची पट्टी आणि लाल रंग/टेपने रोखता येते.',
    'এটি তামার স্ট্রিপ এবং লাল রঙ/টেপ দিয়ে ব্লক করা যেতে পারে।',
    'ಇದನ್ನು ತಾಮ್ರದ ಪಟ್ಟಿ ಮತ್ತು ಕೆಂಪು ಬಣ್ಣ/ಟೇಪ್‌ನಿಂದ ತಡೆಯಬಹುದು.',
    'இதை செம்பு பட்டை மற்றும் சிவப்பு நிற டேப்பால் தடுக்கலாம்.',
    'దీన్ని రాగి పట్టీ మరియు ఎరుపు రంగు/టేప్‌తో నిరోధించవచ్చు.'),
};

export const ENTRANCE_ANALYSIS_DEFAULT_DATA = [
  // ── NORTH ──
  {
    id: 1, zone: "N1", remedies: "negative",
    impact: tr(
      'It increases the unnecessary fear in residents mind and also increases the possibility of murder of its resident.',
      'यह निवासियों के मन में अनावश्यक भय बढ़ाता है और निवासी की हत्या की संभावना भी बढ़ाता है।',
      'તે રહેવાસીઓના મનમાં બિનજરૂરી ડર વધારે છે અને રહેવાસીની હત્યાની શક્યતા પણ વધારે છે.',
      'हे रहिवाशांच्या मनात अनावश्यक भीती वाढवते आणि रहिवाशाच्या हत्येची शक्यताही वाढवते.',
      'এটি বাসিন্দাদের মনে অপ্রয়োজনীয় ভয় বাড়ায় এবং বাসিন্দার হত্যার সম্ভাবনাও বাড়ায়।',
      'ಇದು ನಿವಾಸಿಗಳ ಮನಸ್ಸಿನಲ್ಲಿ ಅನಗತ್ಯ ಭಯವನ್ನು ಹೆಚ್ಚಿಸುತ್ತದೆ ಮತ್ತು ನಿವಾಸಿಯ ಕೊಲೆಯ ಸಾಧ್ಯತೆಯನ್ನೂ ಹೆಚ್ಚಿಸುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களின் மனதில் தேவையற்ற பயத்தை அதிகரிக்கிறது மற்றும் குடியிருப்பாளரின் கொலை நிகழும் வாய்ப்பையும் அதிகரிக்கிறது.',
      'ఇది నివాసితుల మనసులో అనవసరమైన భయాన్ని పెంచుతుంది మరియు నివాసి హత్యకు గురయ్యే అవకాశాన్ని కూడా పెంచుతుంది.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 2, zone: "N2", remedies: "negative",
    impact: tr(
      'It makes the residents jealous from one another. The external people also hold grudge for people residing in these houses.',
      'यह निवासियों को एक-दूसरे से ईर्ष्यालु बनाता है। बाहरी लोग भी इन घरों में रहने वालों से द्वेष रखते हैं।',
      'તે રહેવાસીઓને એકબીજાથી ઈર્ષ્યાળુ બનાવે છે. બહારના લોકો પણ આ ઘરોમાં રહેતા લોકો પ્રત્યે દ્વેષ રાખે છે.',
      'हे रहिवाशांना एकमेकांचा हेवा वाटायला लावते. बाहेरील लोकही या घरात राहणाऱ्यांबद्दल द्वेष बाळगतात.',
      'এটি বাসিন্দাদের একে অপরের প্রতি ঈর্ষান্বিত করে তোলে। বহিরাগতরাও এই বাড়িতে বসবাসকারীদের প্রতি বিদ্বেষ পোষণ করে।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಒಬ್ಬರಿಗೊಬ್ಬರು ಅಸೂಯೆಪಡುವಂತೆ ಮಾಡುತ್ತದೆ. ಹೊರಗಿನವರೂ ಈ ಮನೆಗಳಲ್ಲಿ ವಾಸಿಸುವವರ ಬಗ್ಗೆ ದ್ವೇಷ ಇಟ್ಟುಕೊಳ್ಳುತ್ತಾರೆ.',
      'இது குடியிருப்பாளர்களை ஒருவருக்கொருவர் பொறாமைப்படும்படி செய்கிறது. வெளியில் உள்ளவர்களும் இந்த வீடுகளில் வசிப்பவர்கள் மீது வெறுப்பு கொள்கிறார்கள்.',
      'ఇది నివాసితులను ఒకరిపై ఒకరు అసూయపడేలా చేస్తుంది. బయటి వ్యక్తులు కూడా ఈ ఇళ్లలో నివసించే వారిపై ద్వేషం పెంచుకుంటారు.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 3, zone: "N3", remedies: "positive",
    impact: tr(
      'It gives abundance of money to its residents, More male children are born in homes having this entrance.',
      'यह निवासियों को धन की प्रचुरता देता है, इस प्रवेश द्वार वाले घरों में अधिक पुत्र जन्म लेते हैं।',
      'તે રહેવાસીઓને પુષ્કળ ધન આપે છે, આ પ્રવેશદ્વારવાળા ઘરોમાં વધુ પુત્રોનો જન્મ થાય છે.',
      'हे रहिवाशांना विपुल धन देते, या प्रवेशद्वार असलेल्या घरांमध्ये अधिक मुलगे जन्माला येतात.',
      'এটি বাসিন্দাদের প্রচুর অর্থ দেয়, এই প্রবেশদ্বারযুক্ত বাড়িতে বেশি পুত্র সন্তান জন্ম নেয়।',
      'ಇದು ನಿವಾಸಿಗಳಿಗೆ ಸಮೃದ್ಧ ಹಣವನ್ನು ನೀಡುತ್ತದೆ, ಈ ಪ್ರವೇಶದ್ವಾರವಿರುವ ಮನೆಗಳಲ್ಲಿ ಹೆಚ್ಚು ಗಂಡು ಮಕ್ಕಳು ಜನಿಸುತ್ತಾರೆ.',
      'இது குடியிருப்பாளர்களுக்கு ஏராளமான பணத்தை அளிக்கிறது, இந்த நுழைவாயில் உள்ள வீடுகளில் அதிக ஆண் குழந்தைகள் பிறக்கின்றனர்.',
      'ఇది నివాసితులకు సమృద్ధిగా డబ్బును ఇస్తుంది, ఈ ప్రవేశ ద్వారం ఉన్న ఇళ్లలో ఎక్కువ మగ పిల్లలు జన్మిస్తారు.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 4, zone: "N4", remedies: "positive",
    impact: tr(
      'It gives abundance of inherited property.',
      'यह पैतृक संपत्ति की प्रचुरता देता है।',
      'તે વારસાગત મિલકતની પુષ્કળતા આપે છે.',
      'हे वारसाहक्काने मिळालेल्या मालमत्तेची विपुलता देते.',
      'এটি উত্তরাধিকারসূত্রে প্রাপ্ত সম্পত্তির প্রাচুর্য দেয়।',
      'ಇದು ಪಿತ್ರಾರ್ಜಿತ ಆಸ್ತಿಯ ಸಮೃದ್ಧಿಯನ್ನು ನೀಡುತ್ತದೆ.',
      'இது பரம்பரை சொத்தின் மிகுதியை அளிக்கிறது.',
      'ఇది వారసత్వ ఆస్తి సమృద్ధిని ఇస్తుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 5, zone: "N5", remedies: "neutral",
    impact: tr(
      'It gives a religious bent of mind to its residents.',
      'यह निवासियों को धार्मिक प्रवृत्ति का मन देता है।',
      'તે રહેવાસીઓને ધાર્મિક વલણ આપે છે.',
      'हे रहिवाशांना धार्मिक प्रवृत्ती देते.',
      'এটি বাসিন্দাদের ধর্মীয় মানসিকতা প্রদান করে।',
      'ಇದು ನಿವಾಸಿಗಳಿಗೆ ಧಾರ್ಮಿಕ ಮನೋಭಾವವನ್ನು ನೀಡುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களுக்கு மத நாட்டத்தை அளிக்கிறது.',
      'ఇది నివాసితులకు మతపరమైన మనస్తత్వాన్ని ఇస్తుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 6, zone: "N6", remedies: "negative",
    impact: tr(
      'People living in homes having this entrance are filled excessive anxiety and have rude behaviour.',
      'इस प्रवेश द्वार वाले घरों में रहने वाले लोग अत्यधिक चिंता से भरे होते हैं और उनका व्यवहार रूखा होता है।',
      'આ પ્રવેશદ્વારવાળા ઘરોમાં રહેતા લોકો અતિશય ચિંતાથી ભરેલા હોય છે અને તેમનું વર્તન અસભ્ય હોય છે.',
      'या प्रवेशद्वार असलेल्या घरांमध्ये राहणारे लोक अतिरिक्त चिंतेने भरलेले असतात आणि त्यांचे वागणे उद्धट असते.',
      'এই প্রবেশদ্বারযুক্ত বাড়িতে বসবাসকারী মানুষ অতিরিক্ত উদ্বেগে ভরা থাকে এবং তাদের আচরণ রূঢ় হয়।',
      'ಈ ಪ್ರವೇಶದ್ವಾರವಿರುವ ಮನೆಗಳಲ್ಲಿ ವಾಸಿಸುವ ಜನರು ಅತಿಯಾದ ಆತಂಕದಿಂದ ತುಂಬಿರುತ್ತಾರೆ ಮತ್ತು ಅಸಭ್ಯ ವರ್ತನೆ ಹೊಂದಿರುತ್ತಾರೆ.',
      'இந்த நுழைவாயில் உள்ள வீடுகளில் வசிப்பவர்கள் அதிக பதற்றத்துடன் இருப்பார்கள் மற்றும் முரட்டுத்தனமான நடத்தை கொண்டிருப்பார்கள்.',
      'ఈ ప్రవేశ ద్వారం ఉన్న ఇళ్లలో నివసించే వ్యక్తులు అధిక ఆందోళనతో నిండి ఉంటారు మరియు మొరటుగా ప్రవర్తిస్తారు.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 7, zone: "N7", remedies: "negative",
    impact: tr(
      'Female defy their family traditions.',
      'महिलाएं अपनी पारिवारिक परंपराओं की अवहेलना करती हैं।',
      'સ્ત્રીઓ તેમની પારિવારિક પરંપરાઓની અવગણના કરે છે.',
      'स्त्रिया त्यांच्या कौटुंबिक परंपरांचा अवमान करतात.',
      'নারীরা তাদের পারিবারিক ঐতিহ্য অমান্য করে।',
      'ಮಹಿಳೆಯರು ತಮ್ಮ ಕುಟುಂಬದ ಸಂಪ್ರದಾಯಗಳನ್ನು ಧಿಕ್ಕರಿಸುತ್ತಾರೆ.',
      'பெண்கள் தங்கள் குடும்ப பாரம்பரியங்களை மீறுகிறார்கள்.',
      'మహిళలు తమ కుటుంబ సంప్రదాయాలను ధిక్కరిస్తారు.'),
    block: BLOCK.ALU_BLUE,
  },
  {
    id: 8, zone: "N8", remedies: "positive",
    impact: tr(
      'It is ideal for organizations and companies dealing with banks as it gives higher bank balance to the inhabitants.',
      'यह बैंकों से जुड़े संगठनों और कंपनियों के लिए आदर्श है क्योंकि यह निवासियों को अधिक बैंक बैलेंस देता है।',
      'તે બેંકો સાથે વ્યવહાર કરતી સંસ્થાઓ અને કંપનીઓ માટે આદર્શ છે કારણ કે તે રહેવાસીઓને વધુ બેંક બેલેન્સ આપે છે.',
      'बँकांशी संबंधित संस्था आणि कंपन्यांसाठी हे आदर्श आहे कारण ते रहिवाशांना जास्त बँक बॅलन्स देते.',
      'এটি ব্যাংকের সাথে কাজ করা সংস্থা ও কোম্পানির জন্য আদর্শ কারণ এটি বাসিন্দাদের উচ্চ ব্যাংক ব্যালেন্স দেয়।',
      'ಬ್ಯಾಂಕುಗಳೊಂದಿಗೆ ವ್ಯವಹರಿಸುವ ಸಂಸ್ಥೆಗಳು ಮತ್ತು ಕಂಪನಿಗಳಿಗೆ ಇದು ಸೂಕ್ತವಾಗಿದೆ ಏಕೆಂದರೆ ಇದು ನಿವಾಸಿಗಳಿಗೆ ಹೆಚ್ಚಿನ ಬ್ಯಾಂಕ್ ಬ್ಯಾಲೆನ್ಸ್ ನೀಡುತ್ತದೆ.',
      'வங்கிகளுடன் தொடர்புடைய நிறுவனங்கள் மற்றும் நிறுவனங்களுக்கு இது ஏற்றது, ஏனெனில் இது குடியிருப்பாளர்களுக்கு அதிக வங்கி இருப்பை அளிக்கிறது.',
      'బ్యాంకులతో వ్యవహరించే సంస్థలు మరియు కంపెనీలకు ఇది అనువైనది ఎందుకంటే ఇది నివాసితులకు అధిక బ్యాంకు నిల్వను ఇస్తుంది.'),
    block: BLOCK.IDEAL,
  },

  // ── EAST ──
  {
    id: 9, zone: "E1", remedies: "negative",
    impact: tr(
      'It causes monetary losses and accidents.',
      'यह धन हानि और दुर्घटनाओं का कारण बनता है।',
      'તે નાણાકીય નુકસાન અને અકસ્માતોનું કારણ બને છે.',
      'यामुळे आर्थिक नुकसान आणि अपघात होतात.',
      'এটি আর্থিক ক্ষতি এবং দুর্ঘটনার কারণ হয়।',
      'ಇದು ಹಣಕಾಸಿನ ನಷ್ಟ ಮತ್ತು ಅಪಘಾತಗಳಿಗೆ ಕಾರಣವಾಗುತ್ತದೆ.',
      'இது பண இழப்புகள் மற்றும் விபத்துகளை ஏற்படுத்துகிறது.',
      'ఇది ఆర్థిక నష్టాలు మరియు ప్రమాదాలకు కారణమవుతుంది.'),
    block: BLOCK.SS_GREEN,
  },
  {
    id: 10, zone: "E2", remedies: "negative",
    impact: tr(
      'It increases the expenses and more girls are born in house with this entrance.',
      'यह खर्च बढ़ाता है और इस प्रवेश द्वार वाले घर में अधिक बेटियां जन्म लेती हैं।',
      'તે ખર્ચ વધારે છે અને આ પ્રવેશદ્વારવાળા ઘરમાં વધુ દીકરીઓનો જન્મ થાય છે.',
      'यामुळे खर्च वाढतो आणि या प्रवेशद्वार असलेल्या घरात अधिक मुली जन्माला येतात.',
      'এটি খরচ বাড়ায় এবং এই প্রবেশদ্বারযুক্ত বাড়িতে বেশি মেয়ে সন্তান জন্ম নেয়।',
      'ಇದು ವೆಚ್ಚವನ್ನು ಹೆಚ್ಚಿಸುತ್ತದೆ ಮತ್ತು ಈ ಪ್ರವೇಶದ್ವಾರವಿರುವ ಮನೆಯಲ್ಲಿ ಹೆಚ್ಚು ಹೆಣ್ಣು ಮಕ್ಕಳು ಜನಿಸುತ್ತಾರೆ.',
      'இது செலவுகளை அதிகரிக்கிறது மற்றும் இந்த நுழைவாயில் உள்ள வீட்டில் அதிக பெண் குழந்தைகள் பிறக்கின்றனர்.',
      'ఇది ఖర్చులను పెంచుతుంది మరియు ఈ ప్రవేశ ద్వారం ఉన్న ఇంట్లో ఎక్కువ ఆడపిల్లలు జన్మిస్తారు.'),
    block: BLOCK.ALU_BLUE,
  },
  {
    id: 11, zone: "E3", remedies: "positive",
    impact: tr(
      'It gives Money, profits and success.',
      'यह धन, लाभ और सफलता देता है।',
      'તે ધન, નફો અને સફળતા આપે છે.',
      'हे पैसा, नफा आणि यश देते.',
      'এটি অর্থ, মুনাফা এবং সাফল্য দেয়।',
      'ಇದು ಹಣ, ಲಾಭ ಮತ್ತು ಯಶಸ್ಸನ್ನು ನೀಡುತ್ತದೆ.',
      'இது பணம், லாபம் மற்றும் வெற்றியை அளிக்கிறது.',
      'ఇది డబ్బు, లాభాలు మరియు విజయాన్ని ఇస్తుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 12, zone: "E4", remedies: "positive",
    impact: tr(
      'It attracts beneficial gains from influential people.',
      'यह प्रभावशाली लोगों से लाभकारी फायदे आकर्षित करता है।',
      'તે પ્રભાવશાળી લોકો પાસેથી ફાયદાકારક લાભો આકર્ષે છે.',
      'हे प्रभावशाली लोकांकडून फायदेशीर लाभ आकर्षित करते.',
      'এটি প্রভাবশালী ব্যক্তিদের কাছ থেকে উপকারী লাভ আকর্ষণ করে।',
      'ಇದು ಪ್ರಭಾವಶಾಲಿ ಜನರಿಂದ ಪ್ರಯೋಜನಕಾರಿ ಲಾಭಗಳನ್ನು ಆಕರ್ಷಿಸುತ್ತದೆ.',
      'இது செல்வாக்குமிக்க நபர்களிடமிருந்து பயனுள்ள ஆதாயங்களை ஈர்க்கிறது.',
      'ఇది ప్రభావవంతమైన వ్యక్తుల నుండి ప్రయోజనకరమైన లాభాలను ఆకర్షిస్తుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 13, zone: "E5", remedies: "negative",
    impact: tr(
      'It makes the residents aggressive.',
      'यह निवासियों को आक्रामक बनाता है।',
      'તે રહેવાસીઓને આક્રમક બનાવે છે.',
      'हे रहिवाशांना आक्रमक बनवते.',
      'এটি বাসিন্দাদের আক্রমণাত্মক করে তোলে।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಆಕ್ರಮಣಕಾರಿಯನ್ನಾಗಿ ಮಾಡುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களை ஆக்ரோஷமாக்குகிறது.',
      'ఇది నివాసితులను దూకుడుగా మారుస్తుంది.'),
    block: BLOCK.SS_GREEN,
  },
  {
    id: 14, zone: "E6", remedies: "negative",
    impact: tr(
      'People living in these homes fail their commitment.',
      'इन घरों में रहने वाले लोग अपनी प्रतिबद्धता पूरी नहीं कर पाते।',
      'આ ઘરોમાં રહેતા લોકો તેમની પ્રતિબદ્ધતા પૂરી કરવામાં નિષ્ફળ જાય છે.',
      'या घरांमध्ये राहणारे लोक त्यांची बांधिलकी पूर्ण करण्यात अपयशी ठरतात.',
      'এই বাড়িতে বসবাসকারী মানুষ তাদের প্রতিশ্রুতি রক্ষা করতে ব্যর্থ হয়।',
      'ಈ ಮನೆಗಳಲ್ಲಿ ವಾಸಿಸುವ ಜನರು ತಮ್ಮ ಬದ್ಧತೆಯನ್ನು ಪೂರೈಸುವಲ್ಲಿ ವಿಫಲರಾಗುತ್ತಾರೆ.',
      'இந்த வீடுகளில் வசிப்பவர்கள் தங்கள் உறுதிமொழியை நிறைவேற்றத் தவறுகிறார்கள்.',
      'ఈ ఇళ్లలో నివసించే వ్యక్తులు తమ నిబద్ధతను నెరవేర్చడంలో విఫలమవుతారు.'),
    block: BLOCK.BRASS_YELLOW,
  },
  {
    id: 15, zone: "E7", remedies: "negative",
    impact: tr(
      'It makes the residents insensitive.',
      'यह निवासियों को असंवेदनशील बनाता है।',
      'તે રહેવાસીઓને અસંવેદનશીલ બનાવે છે.',
      'हे रहिवाशांना असंवेदनशील बनवते.',
      'এটি বাসিন্দাদের অসংবেদনশীল করে তোলে।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಸಂವೇದನಾರಹಿತಗೊಳಿಸುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களை உணர்வற்றவர்களாக ஆக்குகிறது.',
      'ఇది నివాసితులను సున్నితత్వం లేకుండా చేస్తుంది.'),
    block: BLOCK.COPPER_RED,
  },
  {
    id: 16, zone: "E8", remedies: "negative",
    impact: tr(
      'It can cause accidents, financial losses and theft.',
      'इससे दुर्घटनाएं, वित्तीय हानि और चोरी हो सकती है।',
      'તે અકસ્માતો, નાણાકીય નુકસાન અને ચોરીનું કારણ બની શકે છે.',
      'यामुळे अपघात, आर्थिक नुकसान आणि चोरी होऊ शकते.',
      'এটি দুর্ঘটনা, আর্থিক ক্ষতি এবং চুরির কারণ হতে পারে।',
      'ಇದು ಅಪಘಾತಗಳು, ಹಣಕಾಸಿನ ನಷ್ಟ ಮತ್ತು ಕಳ್ಳತನಕ್ಕೆ ಕಾರಣವಾಗಬಹುದು.',
      'இது விபத்துகள், நிதி இழப்புகள் மற்றும் திருட்டை ஏற்படுத்தலாம்.',
      'ఇది ప్రమాదాలు, ఆర్థిక నష్టాలు మరియు దొంగతనానికి కారణం కావచ్చు.'),
    block: BLOCK.COPPER_RED,
  },

  // ── SOUTH ──
  {
    id: 17, zone: "S1", remedies: "negative",
    impact: tr(
      'Your male child may get attracted towards bad company.',
      'आपका पुत्र बुरी संगत की ओर आकर्षित हो सकता है।',
      'તમારો પુત્ર ખરાબ સંગત તરફ આકર્ષાઈ શકે છે.',
      'तुमचा मुलगा वाईट संगतीकडे आकर्षित होऊ शकतो.',
      'আপনার পুত্র সন্তান খারাপ সঙ্গের প্রতি আকৃষ্ট হতে পারে।',
      'ನಿಮ್ಮ ಗಂಡು ಮಗು ಕೆಟ್ಟ ಸಹವಾಸದ ಕಡೆಗೆ ಆಕರ್ಷಿತವಾಗಬಹುದು.',
      'உங்கள் ஆண் குழந்தை கெட்ட நண்பர்களால் ஈர்க்கப்படலாம்.',
      'మీ మగ పిల్లవాడు చెడు స్నేహితుల వైపు ఆకర్షితుడు కావచ్చు.'),
    block: BLOCK.COPPER_RED,
  },
  {
    id: 18, zone: "S2", remedies: "positive",
    impact: tr(
      'It is good for people working in MNCs; brings growth, regular promotions and salary hikes.',
      'यह बहुराष्ट्रीय कंपनियों में काम करने वालों के लिए अच्छा है; इससे विकास, नियमित पदोन्नति और वेतन वृद्धि होती है।',
      'તે MNCમાં કામ કરતા લોકો માટે સારું છે; તે વૃદ્ધિ, નિયમિત બઢતી અને પગાર વધારો લાવે છે.',
      'बहुराष्ट्रीय कंपन्यांमध्ये काम करणाऱ्यांसाठी हे चांगले आहे; यामुळे वाढ, नियमित पदोन्नती आणि पगारवाढ मिळते.',
      'এটি বহুজাতিক কোম্পানিতে কর্মরত ব্যক্তিদের জন্য ভালো; এটি প্রবৃদ্ধি, নিয়মিত পদোন্নতি এবং বেতন বৃদ্ধি আনে।',
      'MNCಗಳಲ್ಲಿ ಕೆಲಸ ಮಾಡುವವರಿಗೆ ಇದು ಒಳ್ಳೆಯದು; ಇದು ಬೆಳವಣಿಗೆ, ನಿಯಮಿತ ಬಡ್ತಿ ಮತ್ತು ಸಂಬಳ ಹೆಚ್ಚಳವನ್ನು ತರುತ್ತದೆ.',
      'பன்னாட்டு நிறுவனங்களில் பணிபுரிபவர்களுக்கு இது நல்லது; இது வளர்ச்சி, முறையான பதவி உயர்வு மற்றும் சம்பள உயர்வை தருகிறது.',
      'MNCలలో పనిచేసే వారికి ఇది మంచిది; ఇది వృద్ధి, క్రమమైన పదోన్నతులు మరియు జీతం పెరుగుదలను తెస్తుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 19, zone: "S3", remedies: "positive",
    impact: tr(
      'It gives money and success; residents earn wealth with name and goodwill.',
      'यह धन और सफलता देता है; निवासी नाम और सद्भावना के साथ धन कमाते हैं।',
      'તે ધન અને સફળતા આપે છે; રહેવાસીઓ નામ અને પ્રતિષ્ઠા સાથે સંપત્તિ કમાય છે.',
      'हे पैसा आणि यश देते; रहिवासी नाव आणि प्रतिष्ठेसह संपत्ती कमावतात.',
      'এটি অর্থ এবং সাফল্য দেয়; বাসিন্দারা নাম ও সুনামের সাথে সম্পদ অর্জন করে।',
      'ಇದು ಹಣ ಮತ್ತು ಯಶಸ್ಸನ್ನು ನೀಡುತ್ತದೆ; ನಿವಾಸಿಗಳು ಹೆಸರು ಮತ್ತು ಖ್ಯಾತಿಯೊಂದಿಗೆ ಸಂಪತ್ತನ್ನು ಗಳಿಸುತ್ತಾರೆ.',
      'இது பணம் மற்றும் வெற்றியை அளிக்கிறது; குடியிருப்பாளர்கள் பெயர் மற்றும் நல்லெண்ணத்துடன் செல்வத்தை ஈட்டுகிறார்கள்.',
      'ఇది డబ్బు మరియు విజయాన్ని ఇస్తుంది; నివాసితులు పేరు మరియు ప్రతిష్టతో సంపదను సంపాదిస్తారు.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 20, zone: "S4", remedies: "positive",
    impact: tr(
      'It gives money through fame; ideal for actors and media professionals.',
      'यह प्रसिद्धि के माध्यम से धन देता है; अभिनेताओं और मीडिया पेशेवरों के लिए आदर्श है।',
      'તે ખ્યાતિ દ્વારા ધન આપે છે; અભિનેતાઓ અને મીડિયા વ્યાવસાયિકો માટે આદર્શ છે.',
      'हे प्रसिद्धीद्वारे पैसा देते; कलाकार आणि माध्यम व्यावसायिकांसाठी आदर्श आहे.',
      'এটি খ্যাতির মাধ্যমে অর্থ দেয়; অভিনেতা এবং মিডিয়া পেশাদারদের জন্য আদর্শ।',
      'ಇದು ಖ್ಯಾತಿಯ ಮೂಲಕ ಹಣವನ್ನು ನೀಡುತ್ತದೆ; ನಟರು ಮತ್ತು ಮಾಧ್ಯಮ ವೃತ್ತಿಪರರಿಗೆ ಸೂಕ್ತವಾಗಿದೆ.',
      'இது புகழ் மூலம் பணத்தை அளிக்கிறது; நடிகர்கள் மற்றும் ஊடகத்துறை நிபுணர்களுக்கு ஏற்றது.',
      'ఇది కీర్తి ద్వారా డబ్బును ఇస్తుంది; నటులు మరియు మీడియా నిపుణులకు అనువైనది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 21, zone: "S5", remedies: "negative",
    impact: tr(
      'It gives debts and financial losses.',
      'यह कर्ज और वित्तीय हानि देता है।',
      'તે દેવું અને નાણાકીય નુકસાન આપે છે.',
      'हे कर्ज आणि आर्थिक नुकसान देते.',
      'এটি ঋণ এবং আর্থিক ক্ষতি দেয়।',
      'ಇದು ಸಾಲ ಮತ್ತು ಹಣಕಾಸಿನ ನಷ್ಟವನ್ನು ನೀಡುತ್ತದೆ.',
      'இது கடன்களையும் நிதி இழப்புகளையும் தருகிறது.',
      'ఇది అప్పులు మరియు ఆర్థిక నష్టాలను ఇస్తుంది.'),
    block: BLOCK.BRASS_YELLOW,
  },
  {
    id: 22, zone: "S6", remedies: "negative",
    impact: tr(
      'It brings poverty; this is the most negative entrance being in the direction of disposal.',
      'यह गरीबी लाता है; यह निपटान की दिशा में होने के कारण सबसे नकारात्मक प्रवेश द्वार है।',
      'તે ગરીબી લાવે છે; નિકાલની દિશામાં હોવાથી આ સૌથી નકારાત્મક પ્રવેશદ્વાર છે.',
      'हे गरिबी आणते; विसर्जनाच्या दिशेने असल्याने हे सर्वात नकारात्मक प्रवेशद्वार आहे.',
      'এটি দারিদ্র্য নিয়ে আসে; নিষ্কাশনের দিকে থাকায় এটি সবচেয়ে নেতিবাচক প্রবেশদ্বার।',
      'ಇದು ಬಡತನವನ್ನು ತರುತ್ತದೆ; ವಿಲೇವಾರಿಯ ದಿಕ್ಕಿನಲ್ಲಿ ಇರುವುದರಿಂದ ಇದು ಅತ್ಯಂತ ನಕಾರಾತ್ಮಕ ಪ್ರವೇಶದ್ವಾರವಾಗಿದೆ.',
      'இது வறுமையை கொண்டு வருகிறது; கழிவு அகற்றும் திசையில் இருப்பதால் இது மிகவும் எதிர்மறையான நுழைவாயில் ஆகும்.',
      'ఇది పేదరికాన్ని తెస్తుంది; పారవేసే దిశలో ఉండటం వల్ల ఇది అత్యంత ప్రతికూలమైన ప్రవేశ ద్వారం.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 23, zone: "S7", remedies: "negative",
    impact: tr(
      'It causes wastage of efforts.',
      'यह प्रयासों की बर्बादी का कारण बनता है।',
      'તે પ્રયત્નોનો બગાડ કરે છે.',
      'यामुळे प्रयत्नांचा अपव्यय होतो.',
      'এটি প্রচেষ্টার অপচয় ঘটায়।',
      'ಇದು ಪ್ರಯತ್ನಗಳ ವ್ಯರ್ಥಕ್ಕೆ ಕಾರಣವಾಗುತ್ತದೆ.',
      'இது முயற்சிகளை வீணடிக்கிறது.',
      'ఇది కృషిని వృథా చేస్తుంది.'),
    block: BLOCK.COPPER_RED,
  },
  {
    id: 24, zone: "S8", remedies: "negative",
    impact: tr(
      'It makes the residents lonely and disconnected from the world.',
      'यह निवासियों को अकेला और दुनिया से कटा हुआ बना देता है।',
      'તે રહેવાસીઓને એકલા અને દુનિયાથી અલગ બનાવે છે.',
      'हे रहिवाशांना एकटे आणि जगापासून तुटलेले बनवते.',
      'এটি বাসিন্দাদের একাকী এবং বিশ্ব থেকে বিচ্ছিন্ন করে দেয়।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಒಂಟಿಯಾಗಿ ಮತ್ತು ಜಗತ್ತಿನಿಂದ ದೂರವಾಗಿಸುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களை தனிமையாகவும் உலகத்திலிருந்து துண்டிக்கப்பட்டவர்களாகவும் ஆக்குகிறது.',
      'ఇది నివాసితులను ఒంటరిగా మరియు ప్రపంచం నుండి దూరంగా చేస్తుంది.'),
    block: BLOCK.COPPER_RED,
  },

  // ── WEST ──
  {
    id: 25, zone: "W1", remedies: "negative",
    impact: tr(
      'It gives poor financial condition and reduces life span. Residents are more prone to die at the age of 35 to 40.',
      'यह खराब आर्थिक स्थिति देता है और जीवनकाल कम करता है। निवासियों की 35 से 40 वर्ष की आयु में मृत्यु की संभावना अधिक होती है।',
      'તે નબળી નાણાકીય સ્થિતિ આપે છે અને આયુષ્ય ઘટાડે છે. રહેવાસીઓ 35 થી 40 વર્ષની ઉંમરે મૃત્યુ પામવાની શક્યતા વધુ હોય છે.',
      'हे खराब आर्थिक स्थिती देते आणि आयुष्य कमी करते. रहिवाशांचा 35 ते 40 वयोगटात मृत्यू होण्याची शक्यता जास्त असते.',
      'এটি দুর্বল আর্থিক অবস্থা দেয় এবং আয়ু কমায়। বাসিন্দাদের ৩৫ থেকে ৪০ বছর বয়সে মৃত্যুর সম্ভাবনা বেশি থাকে।',
      'ಇದು ಕಳಪೆ ಆರ್ಥಿಕ ಸ್ಥಿತಿಯನ್ನು ನೀಡುತ್ತದೆ ಮತ್ತು ಜೀವಿತಾವಧಿಯನ್ನು ಕಡಿಮೆ ಮಾಡುತ್ತದೆ. ನಿವಾಸಿಗಳು 35 ರಿಂದ 40 ವರ್ಷ ವಯಸ್ಸಿನಲ್ಲಿ ಸಾಯುವ ಸಾಧ್ಯತೆ ಹೆಚ್ಚು.',
      'இது மோசமான நிதி நிலையை அளிக்கிறது மற்றும் ஆயுளைக் குறைக்கிறது. குடியிருப்பாளர்கள் 35 முதல் 40 வயதிற்குள் இறக்கும் வாய்ப்பு அதிகம்.',
      'ఇది పేద ఆర్థిక పరిస్థితిని ఇస్తుంది మరియు జీవితకాలాన్ని తగ్గిస్తుంది. నివాసితులు 35 నుండి 40 సంవత్సరాల వయస్సులో మరణించే అవకాశం ఎక్కువ.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 26, zone: "W2", remedies: "negative",
    impact: tr(
      'It gives unstable career and insecurity, resulting in relationship problems.',
      'यह अस्थिर करियर और असुरक्षा देता है, जिससे संबंधों में समस्याएं आती हैं।',
      'તે અસ્થિર કારકિર્દી અને અસુરક્ષા આપે છે, જેના પરિણામે સંબંધોમાં સમસ્યાઓ થાય છે.',
      'हे अस्थिर करिअर आणि असुरक्षितता देते, ज्यामुळे नातेसंबंधांमध्ये समस्या येतात.',
      'এটি অস্থির কর্মজীবন এবং নিরাপত্তাহীনতা দেয়, যার ফলে সম্পর্কের সমস্যা দেখা দেয়।',
      'ಇದು ಅಸ್ಥಿರ ವೃತ್ತಿ ಮತ್ತು ಅಭದ್ರತೆಯನ್ನು ನೀಡುತ್ತದೆ, ಇದರ ಪರಿಣಾಮವಾಗಿ ಸಂಬಂಧದ ಸಮಸ್ಯೆಗಳು ಉಂಟಾಗುತ್ತವೆ.',
      'இது நிலையற்ற தொழிலையும் பாதுகாப்பின்மையையும் அளிக்கிறது, இதனால் உறவுச் சிக்கல்கள் ஏற்படுகின்றன.',
      'ఇది అస్థిర వృత్తిని మరియు అభద్రతను ఇస్తుంది, దీని ఫలితంగా సంబంధ సమస్యలు తలెత్తుతాయి.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 27, zone: "W3", remedies: "positive",
    impact: tr(
      'It is auspicious & gives money, growth and prosperity.',
      'यह शुभ है और धन, विकास और समृद्धि देता है।',
      'તે શુભ છે અને ધન, વૃદ્ધિ અને સમૃદ્ધિ આપે છે.',
      'हे शुभ आहे आणि पैसा, वाढ आणि समृद्धी देते.',
      'এটি শুভ এবং অর্থ, প্রবৃদ্ধি এবং সমৃদ্ধি দেয়।',
      'ಇದು ಶುಭಕರವಾಗಿದೆ ಮತ್ತು ಹಣ, ಬೆಳವಣಿಗೆ ಮತ್ತು ಸಮೃದ್ಧಿಯನ್ನು ನೀಡುತ್ತದೆ.',
      'இது சுபமானது & பணம், வளர்ச்சி மற்றும் செழிப்பை அளிக்கிறது.',
      'ఇది శుభప్రదమైనది & డబ్బు, వృద్ధి మరియు సౌభాగ్యాన్ని ఇస్తుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 28, zone: "W4", remedies: "positive",
    impact: tr(
      'It makes the residents happy in their lives.',
      'यह निवासियों को उनके जीवन में खुश रखता है।',
      'તે રહેવાસીઓને તેમના જીવનમાં ખુશ રાખે છે.',
      'हे रहिवाशांना त्यांच्या आयुष्यात आनंदी ठेवते.',
      'এটি বাসিন্দাদের জীবনে সুখী করে তোলে।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಅವರ ಜೀವನದಲ್ಲಿ ಸಂತೋಷವಾಗಿಡುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களை அவர்களின் வாழ்க்கையில் மகிழ்ச்சியாக்குகிறது.',
      'ఇది నివాసితులను వారి జీవితంలో సంతోషంగా ఉంచుతుంది.'),
    block: BLOCK.IDEAL,
  },
  {
    id: 29, zone: "W5", remedies: "negative",
    impact: tr(
      'It gives over ambitious attitude. Residents demand more without focusing on quality.',
      'यह अत्यधिक महत्वाकांक्षी रवैया देता है। निवासी गुणवत्ता पर ध्यान दिए बिना अधिक की मांग करते हैं।',
      'તે અતિ મહત્વાકાંક્ષી વલણ આપે છે. રહેવાસીઓ ગુણવત્તા પર ધ્યાન આપ્યા વિના વધુની માંગ કરે છે.',
      'हे अतिमहत्त्वाकांक्षी वृत्ती देते. रहिवासी गुणवत्तेकडे लक्ष न देता अधिकची मागणी करतात.',
      'এটি অতি উচ্চাকাঙ্ক্ষী মনোভাব দেয়। বাসিন্দারা গুণমানের দিকে মনোযোগ না দিয়ে বেশি দাবি করে।',
      'ಇದು ಅತಿಯಾದ ಮಹತ್ವಾಕಾಂಕ್ಷೆಯ ಮನೋಭಾವವನ್ನು ನೀಡುತ್ತದೆ. ನಿವಾಸಿಗಳು ಗುಣಮಟ್ಟದ ಬಗ್ಗೆ ಗಮನ ಹರಿಸದೆ ಹೆಚ್ಚಿನದನ್ನು ಬಯಸುತ್ತಾರೆ.',
      'இது அதிக லட்சிய மனப்பான்மையை அளிக்கிறது. குடியிருப்பாளர்கள் தரத்தில் கவனம் செலுத்தாமல் அதிகமாக கோருகிறார்கள்.',
      'ఇది అతి ఆశయపూరిత వైఖరిని ఇస్తుంది. నివాసితులు నాణ్యతపై దృష్టి పెట్టకుండా ఎక్కువ డిమాండ్ చేస్తారు.'),
    block: BLOCK.BRASS_YELLOW,
  },
  {
    id: 30, zone: "W6", remedies: "negative",
    impact: tr(
      'It causes unfulfilled expectations.',
      'यह अधूरी उम्मीदों का कारण बनता है।',
      'તે અધૂરી અપેક્ષાઓનું કારણ બને છે.',
      'यामुळे अपूर्ण अपेक्षा राहतात.',
      'এটি অপূর্ণ প্রত্যাশার কারণ হয়।',
      'ಇದು ಈಡೇರದ ನಿರೀಕ್ಷೆಗಳಿಗೆ ಕಾರಣವಾಗುತ್ತದೆ.',
      'இது நிறைவேறாத எதிர்பார்ப்புகளை ஏற்படுத்துகிறது.',
      'ఇది నెరవేరని అంచనాలకు కారణమవుతుంది.'),
    block: BLOCK.BRASS_YELLOW,
  },
  {
    id: 31, zone: "W7", remedies: "negative",
    impact: tr(
      'It makes the inhabitants unhappy and prone to drug addiction.',
      'यह निवासियों को दुखी और नशे की लत के प्रति संवेदनशील बनाता है।',
      'તે રહેવાસીઓને નાખુશ અને ડ્રગ્સના વ્યસન તરફ સંવેદનશીલ બનાવે છે.',
      'हे रहिवाशांना दुःखी आणि व्यसनाधीनतेकडे प्रवृत्त करते.',
      'এটি বাসিন্দাদের অসুখী করে এবং মাদকাসক্তির প্রবণতা তৈরি করে।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಅಸಂತೋಷಗೊಳಿಸುತ್ತದೆ ಮತ್ತು ಮಾದಕ ವ್ಯಸನಕ್ಕೆ ಒಳಗಾಗುವಂತೆ ಮಾಡುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களை மகிழ்ச்சியற்றவர்களாகவும் போதைப்பொருள் பழக்கத்திற்கு ஆளாகும் வாய்ப்புள்ளவர்களாகவும் ஆக்குகிறது.',
      'ఇది నివాసితులను అసంతృప్తిగా మరియు మాదక ద్రవ్య వ్యసనానికి గురయ్యేలా చేస్తుంది.'),
    block: BLOCK.IRON_WHITE,
  },
  {
    id: 32, zone: "W8", remedies: "negative",
    impact: tr(
      'It makes the inhabitants selfish.',
      'यह निवासियों को स्वार्थी बनाता है।',
      'તે રહેવાસીઓને સ્વાર્થી બનાવે છે.',
      'हे रहिवाशांना स्वार्थी बनवते.',
      'এটি বাসিন্দাদের স্বার্থপর করে তোলে।',
      'ಇದು ನಿವಾಸಿಗಳನ್ನು ಸ್ವಾರ್ಥಿಗಳನ್ನಾಗಿ ಮಾಡುತ್ತದೆ.',
      'இது குடியிருப்பாளர்களை சுயநலவாதிகளாக ஆக்குகிறது.',
      'ఇది నివాసితులను స్వార్థపరులుగా చేస్తుంది.'),
    block: BLOCK.ALU_BLUE,
  },
];
